import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useFileDownload } from '@risksmart-app/components/src/file/useFileDownload';
import { VersionStatus as Version_Status_Enum } from '@risksmart-app/domain/src/types/consts/version-status';
import { useEffect, useState } from 'react';

import { useGetLatestDocumentFile } from '@/hooks/queries';
import { handleError } from '@/utils/errorUtils';

type GetDocumentFileData = NonNullable<
  ReturnType<typeof useGetLatestDocumentFile>['data']
>;

const escapeFileVersion = (version?: string) =>
  version ? version.replaceAll(/\./g, '_') : '';

export type PublicDocumentData = (
  | {
      type: 'file';
      fileName: string;
      blob?: Blob;
    }
  | {
      type: 'html';
      content: string;
    }
  | {
      type: 'link';
      link: string;
    }
) & {
  documentFile: GetDocumentFileData['document_file'][0];
};

type UsePublicDocumentResult =
  | {
      loading: false;
      error: false;
      data: PublicDocumentData;
    }
  | {
      loading: false;
      error: true;
      data?: undefined;
    }
  | {
      loading: true;
      error: false;
      data?: undefined;
    };

export const usePublicDocument = (
  documentId: string,
  fileId: 'latest' | string
): UsePublicDocumentResult => {
  // The blob is cached together with the FileId it was downloaded for.
  // Client-side navigation between two public-policy files re-renders this
  // hook without remounting (same route pattern), so a bare `Blob` state
  // would keep rendering the previous policy's document under the new
  // policy's title (WOR-107).
  const [download, setDownload] = useState<{ fileId: string; blob: Blob }>();
  const downloadFile = useFileDownload();

  const { data, loading, error } = useGetLatestDocumentFile({
    queryArgs: {
      parentDocumentId: documentId,
      fileId: fileId !== 'latest' ? fileId : undefined,
      status: Version_Status_Enum.Published,
    },
  });

  const documentFile = data?.document_file[0];
  const title = documentFile?.parent?.Title;
  const version = documentFile?.Version;
  const fileName = `${title}_${escapeFileVersion(version)}`;
  const blob =
    documentFile?.FileId && download?.fileId === documentFile.FileId
      ? download.blob
      : undefined;

  useEffect(() => {
    // Cleanup guard: a download superseded by navigation must not land in
    // state — a late resolution would evict the current file's blob and
    // trigger a redundant re-download.
    let isCurrent = true;

    (async () => {
      if (documentFile && documentFile.FileId && version && !blob) {
        const targetFileId = documentFile.FileId;
        try {
          const fetched = await downloadFile(
            { fileId: targetFileId, fileName },
            false
          );
          if (isCurrent) {
            setDownload({ fileId: targetFileId, blob: fetched });
          }
        } catch (error) {
          // Surface the failure instead of leaving an unhandled rejection;
          // the preview keeps its loading state and a remount retries.
          handleError(error);
        }
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [documentFile, downloadFile, fileName, version, blob]);

  if (error && !loading) {
    return { loading, error: !!error };
  }
  if (!error && loading) {
    return { loading, error: !!error };
  }
  if (!documentFile) {
    throw new PageNotFound(`Document version with id ${fileId} not found`);
  }

  switch (documentFile.Type) {
    case 'html':
      return {
        loading: false,
        error: false,
        data: {
          documentFile,
          type: 'html',
          content: documentFile.Content ?? '',
        },
      };
    case 'link':
      return {
        loading: false,
        error: false,
        data: {
          documentFile,
          type: 'link',
          link: documentFile.Link ?? '',
        },
      };
    case 'file':
      return blob || documentFile
        ? {
            loading: false,
            error: false,
            data: {
              documentFile,
              type: 'file',
              fileName: fileName,
              blob: blob,
            },
          }
        : {
            loading: true,
            error: false,
          };
    default:
      return { loading: false, error: true };
  }
};
